import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import api, { apiErr } from "../../lib/api";
import { formatINR, categoryMeta, CATEGORIES, STATUS_META, daysLabel, formatShortDate } from "../../lib/format";
import AddCommitmentDialog from "../../components/app/AddCommitmentDialog";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { MoreVertical, Pause, Play, XCircle, Pencil, Trash2, Plus, Users } from "lucide-react";
import { toast } from "sonner";

export default function Commitments() {
  const { openAdd, refreshKey, triggerRefresh } = useOutletContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/commitments"); setItems(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const changeStatus = async (c, status) => {
    try { await api.patch(`/commitments/${c.id}/status`, { status }); toast.success(`Marked ${status}.`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remindCollect = async (c) => {
    try { const { data } = await api.post(`/commitments/${c.id}/remind-reimbursement`); toast.success(`Reminder added${data.owed_total ? ` · ₹${Math.round(data.owed_total).toLocaleString("en-IN")} owed` : ""}.`); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/commitments/${toDelete.id}`);
      toast.success("Commitment deleted.", { action: { label: "Undo", onClick: async () => {
        const { id, monthly_equivalent, days_remaining, remaining_instalments, ...rest } = toDelete;
        try { await api.post("/commitments", rest); load(); } catch {}
      }}});
      setToDelete(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const filtered = items.filter((c) =>
    (catFilter === "all" || c.category === catFilter) &&
    (statusFilter === "all" || c.status === statusFilter));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-heading text-2xl font-medium text-ink">Commitments</h1>
        <Button onClick={openAdd} className="rounded-full bg-navy text-white hover:bg-navy-light hidden md:inline-flex" data-testid="commitments-add">
          <Plus size={16} className="mr-1.5" />Add
        </Button>
      </div>

      <div className="flex gap-3 mb-4 overflow-x-auto no-scrollbar">
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="rounded-full w-40 shrink-0" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All categories</SelectItem>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="rounded-full w-36 shrink-0" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="paused">Paused</SelectItem><SelectItem value="cancelled">Cancelled</SelectItem></SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
      ) : filtered.length === 0 ? (
        <Card className="rounded-2xl border-border shadow-card-sm p-10 text-center" data-testid="commitments-empty">
          <p className="text-slatey">No commitments here yet.</p>
          <Button onClick={openAdd} className="rounded-full mt-4 bg-navy text-white hover:bg-navy-light"><Plus size={16} className="mr-1.5" />Add Commitment</Button>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="commitments-list">
          {items.length === 1 && catFilter === "all" && statusFilter === "all" && (
            <Card className="rounded-2xl border-border shadow-card-sm p-4 flex items-center gap-3 bg-mint-light/40" data-testid="tip-commitments-add-more">
              <div className="h-10 w-10 rounded-xl bg-white text-mint flex items-center justify-center shrink-0"><Plus size={18} strokeWidth={1.5} /></div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-ink">Add a few more commitments</p>
                <p className="text-xs text-slatey">You've added one so far. The more you add, the clearer your full commitment position becomes.</p>
              </div>
              <Button size="sm" onClick={openAdd} className="rounded-full bg-navy text-white hover:bg-navy-light shrink-0 h-9" data-testid="tip-commitments-add-btn"><Plus size={14} className="mr-1" />Add</Button>
            </Card>
          )}
          {filtered.map((c) => {
            const meta = categoryMeta(c.category); const Icon = meta.icon;
            const st = STATUS_META[c.status] || STATUS_META.active;
            return (
              <Card key={c.id} className="rounded-2xl border-border shadow-card-sm p-4 flex items-center gap-3" data-testid={`commitment-card-${c.id}`}>
                <div className="h-11 w-11 rounded-xl bg-secondary flex items-center justify-center shrink-0"><Icon size={18} strokeWidth={1.5} className="text-navy" /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-ink truncate">{c.name}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {meta.label} · {formatINR(c.amount)} / {c.frequency}
                    {c.frequency === "annual" && ` · ${formatINR(c.monthly_equivalent)}/mo equiv`}
                  </p>
                  {c.next_due_date && <p className="text-xs text-slatey mt-0.5">{formatShortDate(c.next_due_date)} · {daysLabel(c.days_remaining)}</p>}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><button className="p-2 text-muted-foreground hover:text-ink" data-testid={`commitment-menu-${c.id}`}><MoreVertical size={18} /></button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-xl">
                    <DropdownMenuItem onClick={() => { setEditing(c); setEditOpen(true); }} data-testid={`edit-${c.id}`}><Pencil size={15} className="mr-2" />Edit</DropdownMenuItem>
                    {(c.shared_individual === "shared" || c.category === "shared") && (
                      <DropdownMenuItem onClick={() => remindCollect(c)} data-testid={`remind-${c.id}`}><Users size={15} className="mr-2" />Remind to collect</DropdownMenuItem>
                    )}
                    {c.status !== "active" && <DropdownMenuItem onClick={() => changeStatus(c, "active")}><Play size={15} className="mr-2" />Mark active</DropdownMenuItem>}
                    {c.status === "active" && <DropdownMenuItem onClick={() => changeStatus(c, "paused")} data-testid={`pause-${c.id}`}><Pause size={15} className="mr-2" />Pause</DropdownMenuItem>}
                    {c.status !== "cancelled" && <DropdownMenuItem onClick={() => changeStatus(c, "cancelled")} data-testid={`cancel-${c.id}`}><XCircle size={15} className="mr-2" />Cancel</DropdownMenuItem>}
                    <DropdownMenuItem onClick={() => setToDelete(c)} className="text-calm-alert" data-testid={`delete-${c.id}`}><Trash2 size={15} className="mr-2" />Delete</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </Card>
            );
          })}
        </div>
      )}

      <AddCommitmentDialog open={editOpen} onOpenChange={setEditOpen} existing={editing} onSaved={() => { triggerRefresh(); load(); }} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this commitment?</AlertDialogTitle>
            <AlertDialogDescription>“{toDelete?.name}” will be removed. You can undo this right after.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="rounded-full bg-calm-alert hover:bg-calm-alert/90" data-testid="delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
